import { useState, useEffect } from 'react';
import { useTheme } from 'styled-components';

import { Icon } from 'components/icon';
import { ButtonColors } from 'types/enums';
import { useAlert, useNavigate } from 'hooks';
import { Menu, MenuItem } from 'components/menu';
import { Button, ButtonProps } from 'components/button';
import { useLtlAccessQuery as useStockOrderLtlAccessQuery } from 'store/api/stock-orders';
import { useLtlRussInvoiceLinkQuery } from 'store/api/quickbooks/ltl-russ-invoice-link-api';
import { useLtlAccessQuery as useStockTransferLtlAccessQuery } from 'store/api/stock-transfers';

import * as Styles from './styles';
import { StockLtlRowMenuProps } from './types';
import { textOrNull, russPdfUrl, trackingTarget } from './config';

// The "⋯" button: the same look before and after the menu is loaded.
const useTriggerProps = (): ButtonProps => {
  const theme = useTheme();
  return {
    transparent: true,
    icon: <Icon.More />,
    stopPropagation: true,
    testId: 'stock-row-menu',
    colorType: ButtonColors.BlackWhite,
    // a 32px tap target on tablets and phones
    padding: theme?.responsive?.isDesktop === false ? '4px' : '0',
  };
};

/**
 * T-F07: "⋯" at the end of a stock order / stock transfer row. Each item
 * shows only when it applies. Nothing is requested while the menu is
 * closed. On open: the record's Russ PDF link (one GET
 * /ltl/russ-invoice-link, only when the record has an invoice #) and, only
 * where Edit LTL may apply, the same L4a probe as the details page (cached
 * per record).
 */
const RowMenu = ({
  record,
  onEditLtl,
  sourceType,
  canEditLtl,
  detailsPath,
  onUpdateShipping,
}: StockLtlRowMenuProps) => {
  const triggerProps = useTriggerProps();
  const navigate = useNavigate();
  const { showErrorAlert, showSuccessAlert } = useAlert();
  // mounted by the first click on "⋯", so it starts open
  const [open, setOpen] = useState(true);

  const invoiceNumber = textOrNull(record.ltlInvoiceNumber);
  const trackingNumber = textOrNull(record.trackingNumber);
  const tracking = trackingTarget(record.carrier, trackingNumber);
  const isOrder = sourceType === 'STOCK_ORDER';

  const russLink = useLtlRussInvoiceLinkQuery(
    { sourceType, sourceId: record.id },
    {
      skip: !open || !invoiceNumber,
      // the PDF link as it is now, each time the menu opens (one request)
      refetchOnMountOrArgChange: true,
    }
  );
  const orderProbe = useStockOrderLtlAccessQuery(record.id, {
    skip: !open || !canEditLtl || !isOrder,
  });
  const transferProbe = useStockTransferLtlAccessQuery(record.id, {
    skip: !open || !canEditLtl || isOrder,
  });
  const probe = isOrder ? orderProbe : transferProbe;

  const pdfUrl = russLink.isError
    ? null
    : russPdfUrl(russLink.currentData ?? russLink.data);

  // writeText starts synchronously, while this page still has focus: the
  // browser refuses a copy from a page that has lost focus (e.g. to the tab
  // window.open just opened).
  const copy = (text: string, done: string, failed: string) => {
    let writing: Promise<void>;
    try {
      writing = navigator.clipboard.writeText(text);
    } catch (error) {
      writing = Promise.reject(error);
    }
    return writing
      .then(() => showSuccessAlert(done))
      .catch(() => showErrorAlert(failed));
  };

  const items: MenuItem[] = [];

  if (canEditLtl && (probe.isSuccess || probe.isFetching)) {
    items.push({
      text: 'Edit LTL',
      onClick: onEditLtl,
      icon: <Icon.Edit />,
      // the L4a probe has not answered yet
      disabled: !probe.isSuccess,
      testId: 'stock-row-edit-ltl',
    });
  }

  if (onUpdateShipping) {
    items.push({
      icon: <Icon.Edit />,
      onClick: onUpdateShipping,
      text: 'Update shipping info',
      testId: 'stock-row-update-shipping',
    });
  }

  if (tracking && trackingNumber) {
    items.push({
      icon: <Icon.Link />,
      text: 'Track shipment',
      testId: 'stock-row-track',
      onClick: () => {
        if (tracking.copy) {
          // the carrier page may ask for the number: have it ready to paste;
          // copy before opening it, while this page still has focus
          copy(
            trackingNumber,
            tracking.withNumber
              ? `Tracking # ${trackingNumber} copied`
              : "Tracking # copied — paste it on the carrier's page",
            'Could not copy the tracking #'
          );
        }
        window.open(tracking.url, '_blank', 'noopener,noreferrer');
      },
    });
  }

  if (trackingNumber) {
    items.push({
      icon: <Icon.Copy />,
      text: 'Copy tracking #',
      testId: 'stock-row-copy-tracking',
      onClick: () =>
        copy(
          trackingNumber,
          `Tracking # ${trackingNumber} copied`,
          'Could not copy the tracking #'
        ),
    });
  }

  if (invoiceNumber) {
    items.push({
      icon: <Icon.Copy />,
      text: 'Copy invoice #',
      testId: 'stock-row-copy-invoice',
      onClick: () =>
        copy(
          invoiceNumber,
          `Invoice # ${invoiceNumber} copied`,
          'Could not copy the invoice #'
        ),
    });
  }

  if (pdfUrl) {
    items.push({
      icon: <Icon.FileFilled />,
      text: 'View Russ invoice',
      testId: 'stock-row-russ-pdf',
      onClick: () => window.open(pdfUrl, '_blank', 'noopener,noreferrer'),
    });
  }

  items.push({
    icon: <Icon.Eye />,
    text: 'Open details',
    testId: 'stock-row-details',
    onClick: () => navigate(detailsPath),
  });

  // The menu's size changes when the answers arrive: let the popup place
  // itself again so it stays inside the screen.
  const itemsKey = items.map((item) => `${item.text}:${item.disabled}`).join();
  useEffect(() => {
    if (open) {
      window.dispatchEvent(new Event('resize'));
    }
  }, [open, itemsKey]);

  return (
    <Styles.Wrap>
      <Menu
        defaultOpen
        data={items}
        width="232px"
        buttonAlignStart
        keepTooltipInside
        buttonProps={triggerProps}
        onOpen={() => setOpen(true)}
        onClose={() => setOpen(false)}
        position={['bottom right', 'top right']}
      />
    </Styles.Wrap>
  );
};

/**
 * Each row renders only the "⋯" button; the menu (popup, requests, alerts)
 * mounts on the first click, so the list renders as fast as before.
 */
export const StockLtlRowMenu = (props: StockLtlRowMenuProps) => {
  const [active, setActive] = useState(false);
  const triggerProps = useTriggerProps();

  if (active) {
    return <RowMenu {...props} />;
  }

  return (
    <Styles.Wrap>
      <Button {...triggerProps} onClick={() => setActive(true)} />
    </Styles.Wrap>
  );
};

export type { StockLtlRowMenuProps } from './types';
export { russPdfUrl, rowMenuCells, trackingTarget } from './config';
